import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  CrosshairPlugin
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Override default legend label color
ChartJS.defaults.plugins.legend.labels.color = '#ffffff';

// Custom plugin for vertical line on hover
const verticalLinePlugin = {
  id: 'verticalLine',
  afterDraw: (chart) => {
    if (chart.tooltip?._active?.length) {
      const activePoint = chart.tooltip._active[0];
      const ctx = chart.ctx;
      const x = activePoint.element.x;
      const topY = chart.scales.y.top;
      const bottomY = chart.scales.y.bottom;

      // Draw vertical line
      ctx.save();
      
      // Main line
      ctx.beginPath();
      ctx.moveTo(x, topY);
      ctx.lineTo(x, bottomY);
      ctx.lineWidth = 2;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
      ctx.stroke();
      
      // Glow effect
      ctx.beginPath();
      ctx.moveTo(x, topY);
      ctx.lineTo(x, bottomY);
      ctx.lineWidth = 4;
      ctx.strokeStyle = 'rgba(247, 147, 26, 0.1)';
      ctx.stroke();
      
      // Draw a circle at the intersection points
      chart.data.datasets.forEach((dataset, i) => {
        if (chart.isDatasetVisible(i) && activePoint.datasetIndex === i) {
          const y = activePoint.element.y;
          
          // Outer glow
          ctx.beginPath();
          ctx.arc(x, y, 8, 0, 2 * Math.PI);
          ctx.fillStyle = 'rgba(247, 147, 26, 0.2)';
          ctx.fill();
          
          // Inner circle
          ctx.beginPath();
          ctx.arc(x, y, 4, 0, 2 * Math.PI);
          ctx.fillStyle = dataset.borderColor;
          ctx.fill();
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });
      
      ctx.restore();
    }
  }
};

ChartJS.register(verticalLinePlugin);

class BitcoinChart extends React.Component {
  getChartData() {
    if (!this.props.prices) {
      return null;
    }

    const labels = [];
    const priceData = [];

    // Investment period — the chart starts at the date chosen in the form.
    const startYearMonth = this.props.startYear * 12 + this.props.startMonth;
    const currentYearMonth = new Date().getFullYear() * 12 + (new Date().getMonth() + 1);

    // Full price history always draws; only the investment lines (below) are
    // clipped to the chosen start date — clipping the price line too left the
    // default view (start = current month) rendering an empty chart.
    const sortedPrices = Object.entries(this.props.prices)
      .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
      .filter(([, data]) => data.price > 0);

    // Generate labels and price data
    sortedPrices.forEach(([yearMonth, data]) => {
      const label = `${data.year}-${String(data.month).padStart(2, '0')}`;
      labels.push(label);
      priceData.push(data.price);
    });

    // The latest stored month is just a snapshot. For the current month use the
    // live current price when valuing things, so the chart's endpoint matches the
    // "Current Value" card. `priceData` (monthly) still drives BTC accumulation so
    // the total BTC stays identical to the cards.
    const lastYearMonth = sortedPrices.length
      ? parseInt(sortedPrices[sortedPrices.length - 1][0])
      : 0;
    const useLivePrice = this.props.currentPrice > 0 && lastYearMonth === currentYearMonth;

    // Display copy of the price line with the final point at the live price.
    const priceLineData = priceData.slice();
    if (useLivePrice) {
      priceLineData[priceLineData.length - 1] = this.props.currentPrice;
    }

    // Calculate cumulative wealth in USD: every payment buys BTC at that month's
    // price; wealth is the running BTC total valued at the current month's price.
    let cumulativeBTC = 0;
    const wealthData = labels.map((label, index) => {
      const [year, month] = label.split('-').map(Number);
      const yearMonth = year * 12 + month;
      const currentPrice = priceData[index];

      if (yearMonth === startYearMonth && this.props.downpayment > 0) {
        cumulativeBTC += this.props.downpayment / currentPrice;
      }
      if (yearMonth > startYearMonth && yearMonth <= currentYearMonth && this.props.monthlyPayment > 0) {
        cumulativeBTC += this.props.monthlyPayment / currentPrice;
      }

      if (yearMonth >= startYearMonth && yearMonth <= currentYearMonth && cumulativeBTC > 0) {
        const valuationPrice = (useLivePrice && yearMonth === currentYearMonth)
          ? this.props.currentPrice
          : currentPrice;
        return cumulativeBTC * valuationPrice;
      }
      return null;
    });

    // House side: cumulative cash paid in, and an approximate home-equity curve.
    // Equity is estimated from a standard 30-year fixed amortization so it starts
    // small (early payments are mostly interest) and accelerates over time.
    const MORTGAGE_ANNUAL_RATE = 0.06; // assumed 30-yr fixed rate for the equity estimate
    const LOAN_TERM_MONTHS = 360;
    const monthlyRate = MORTGAGE_ANNUAL_RATE / 12;
    const monthlyPayment = this.props.monthlyPayment;
    const downpayment = this.props.downpayment;

    // The loan principal a monthly payment of `monthlyPayment` services over the term.
    const loanPrincipal = monthlyPayment > 0
      ? monthlyPayment * (1 - Math.pow(1 + monthlyRate, -LOAN_TERM_MONTHS)) / monthlyRate
      : 0;

    const cashInData = labels.map((label) => {
      const [year, month] = label.split('-').map(Number);
      const yearMonth = year * 12 + month;
      if (yearMonth < startYearMonth || yearMonth > currentYearMonth) return null;
      const monthsElapsed = yearMonth - startYearMonth;
      return downpayment + monthlyPayment * monthsElapsed;
    });

    const equityData = labels.map((label) => {
      const [year, month] = label.split('-').map(Number);
      const yearMonth = year * 12 + month;
      if (yearMonth < startYearMonth || yearMonth > currentYearMonth) return null;
      const monthsElapsed = Math.min(yearMonth - startYearMonth, LOAN_TERM_MONTHS);
      let balance = loanPrincipal;
      for (let k = 0; k < monthsElapsed; k++) {
        const interest = balance * monthlyRate;
        balance -= (monthlyPayment - interest);
        if (balance < 0) balance = 0;
      }
      return downpayment + (loanPrincipal - balance);
    });

    return {
      labels,
      datasets: [
        {
          label: 'Bitcoin Price (USD)',
          data: priceLineData,
          borderColor: '#f7931a',
          backgroundColor: 'rgba(247, 147, 26, 0.1)',
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 5,
          tension: 0.4,
          fill: true,
          legendColor: '#ffffff'
        },
        {
          label: 'Your Wealth in BTC',
          data: wealthData,
          borderColor: '#4CAF50',
          backgroundColor: 'rgba(76, 175, 80, 0.1)',
          borderWidth: 3,
          pointRadius: 0,
          pointHoverRadius: 5,
          tension: 0.4,
          fill: true,
          legendColor: '#ffffff',
          yAxisID: 'y1'
        },
        {
          label: 'Cash Into House',
          data: cashInData,
          borderColor: '#EF5350',
          backgroundColor: 'rgba(239, 83, 80, 0.05)',
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 5,
          tension: 0.4,
          fill: false,
          legendColor: '#ffffff',
          yAxisID: 'y1'
        },
        {
          label: 'Home Equity (est.)',
          data: equityData,
          borderColor: '#42A5F5',
          backgroundColor: 'rgba(66, 165, 245, 0.05)',
          borderWidth: 2,
          borderDash: [6, 4],
          pointRadius: 0,
          pointHoverRadius: 5,
          tension: 0.4,
          fill: false,
          legendColor: '#ffffff',
          yAxisID: 'y1'
        }
      ]
    };
  }

  getChartOptions() {
    const startYearMonth = this.props.startYear * 12 + this.props.startMonth;
    const currentYearMonth = new Date().getFullYear() * 12 + (new Date().getMonth() + 1);

    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: 10,
          right: 10,
          top: 10,
          bottom: 10
        }
      },
      plugins: {
        legend: {
          display: true,
          position: 'top',
          labels: {
            color: '#ffffff',
            font: {
              size: 16,
              weight: 'bold',
              family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            },
            usePointStyle: true,
            padding: 25,
            generateLabels: (chart) => {
              const datasets = chart.data.datasets;
              return datasets.map((dataset, i) => ({
                text: dataset.label,
                fillStyle: dataset.borderColor,
                strokeStyle: dataset.borderColor,
                lineWidth: dataset.borderWidth,
                fontColor: '#ffffff',
                hidden: !chart.isDatasetVisible(i),
                index: i
              }));
            }
          }
        },
        title: {
          display: true,
          text: 'Bitcoin Price History & Your Investment Timeline',
          color: '#ffffff',
          font: {
            size: 18,
            weight: 'bold'
          },
          padding: {
            bottom: 20
          }
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#ffffff',
          bodyColor: '#ffffff',
          borderColor: '#f7931a',
          borderWidth: 1,
          padding: 12,
          displayColors: true,
          callbacks: {
            label: function(context) {
              const value = context.parsed.y;
              if (value === null || value === undefined) return '';
              if (context.dataset.label === 'Bitcoin Price (USD)') {
                return `Price: $${value.toLocaleString()}`;
              }
              return `${context.dataset.label}: $${Math.round(value).toLocaleString()}`;
            }
          }
        },
        annotation: {
          annotations: {
            investmentPeriod: {
              type: 'box',
              xMin: this.getInvestmentStartIndex(),
              xMax: this.getInvestmentEndIndex(),
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              borderColor: 'rgba(76, 175, 80, 0.3)',
              borderWidth: 1
            }
          }
        }
      },
      scales: {
        x: {
          display: true,
          grid: {
            display: false
          },
          ticks: {
            color: '#ffffff',
            maxTicksLimit: 10,
            maxRotation: 45,
            minRotation: 45
          }
        },
        y: {
          display: true,
          type: 'logarithmic',
          position: 'left',
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          },
          ticks: {
            color: '#ffffff',
            maxTicksLimit: 8,
            callback: function(value) {
              if (value >= 1000) {
                return '$' + (value / 1000).toFixed(0) + 'k';
              }
              return '$' + value.toFixed(0);
            }
          },
          title: {
            display: true,
            text: 'Bitcoin Price (USD)',
            color: '#f7931a'
          }
        },
        y1: {
          display: true,
          type: 'logarithmic',
          position: 'right',
          grid: {
            display: false
          },
          ticks: {
            color: '#90EE90',
            maxTicksLimit: 8,
            callback: function(value) {
              if (value >= 1000000) {
                return '$' + (value / 1000000).toFixed(1) + 'M';
              } else if (value >= 1000) {
                return '$' + (value / 1000).toFixed(0) + 'k';
              }
              return '$' + value.toFixed(0);
            }
          },
          title: {
            display: true,
            text: 'USD Value',
            color: '#ffffff'
          }
        }
      },
      interaction: {
        mode: 'index',
        axis: 'x',
        intersect: false
      },
      hover: {
        mode: 'index',
        intersect: false
      }
    };
  }

  getInvestmentStartIndex() {
    if (!this.props.prices) return 0;
    
    const startYearMonth = this.props.startYear * 12 + this.props.startMonth;
    const sortedKeys = Object.keys(this.props.prices)
      .map(Number)
      .sort((a, b) => a - b);
    
    return sortedKeys.findIndex(ym => ym >= startYearMonth);
  }

  getInvestmentEndIndex() {
    if (!this.props.prices) return 0;
    
    const currentYearMonth = new Date().getFullYear() * 12 + (new Date().getMonth() + 1);
    const sortedKeys = Object.keys(this.props.prices)
      .map(Number)
      .sort((a, b) => a - b);
    
    return sortedKeys.findIndex(ym => ym >= currentYearMonth);
  }

  render() {
    const chartData = this.getChartData();
    
    if (!chartData) {
      return null;
    }

    return (
      <div className="chart-container">
        <div className="chart-wrapper">
          <Line data={chartData} options={this.getChartOptions()} />
        </div>
      </div>
    );
  }
}

export default BitcoinChart;